import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Col, Form, Input, Row, Select, Tooltip, message } from "antd";
import type { FormInstance } from "antd/es/form/Form";
import NumberFormat from "react-number-format";
import { Moment } from "moment";
import { useAppDispatch, useAppSelector } from "../../../..";
import { ReactComponent as DeleteIcon } from "../../../../img/delete-round.svg";
import { ReactComponent as CopyIcon } from "../../../../img/copy-icon.svg";
import {
  checkIsLoading,
  getSelectOptions,
  round,
  createObjectWithDefault,
} from "../../../../lib/utils";
import { rangeFromLimitValidator } from "../utils/rangeFromLimitValidator";
import { rangeToLimitValidator } from "../utils/rangeToLimitValidator";
import { taxTsActions } from "../../../../store/slices/taxTs";
import { fakeBalanceType } from "../utils/fakeBalanceType";
import { getAllowedRateTypes } from "../utils/getAllowedRateTypes";
import { getAllowedPriceUnits } from "../utils/getAllowedPriceUnits";
import { hasAccess } from "../../../RolesModule/hasAccess";
import { OFFERING_PRICE_NEGATIVEAMOUNT } from "../../../../lib/const/permissions";
import { AggregationRule } from "../../../../lib/types/AggregationRule";
import {
  IOfferListSelectMode,
  IOfferTarifficationFormData,
} from "../../../../containers/OfferContainer/context/types";
import { getRateTypeId } from "../../../../containers/OfferContainer/utils/getRateTypeId";
import { getPriceUnitId } from "../../../../containers/OfferContainer/utils/getPriceUnitId";
import { RateModes } from "../../../../lib/const/rateModes";
import "./ServiceRateEdit.less";

type FieldName =
  | "name"
  | "balanceTypeId"
  | "rateModeId"
  | "rateTypeId"
  | "priceWithVat"
  | "taxRate"
  | "price"
  | "currencyId"
  // | "ratePeriodId"
  | "priceUnitId"
  | "chargeNameId"
  | "rangeFrom"
  | "rangeTo"
  | "aggregationRuleId";

interface ComponentProps {
  isPacketTariff: boolean;
  form: FormInstance<IOfferTarifficationFormData<Moment>>;
  aggregationRuleList: AggregationRule<"createForm">[] | undefined;
  onRemoveLastRate?: () => void;
  offerListSelectMode?: IOfferListSelectMode;
  windowWidth?: number;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const ServiceRateEdit = (props: ComponentProps) => {
  const {
    isPacketTariff,
    form,
    aggregationRuleList,
    onRemoveLastRate,
    offerListSelectMode,
    windowWidth = 1570,
  } = props;
  const {
    rateType,
    rateTypeLoading,
    chargeNames,
    chargeNamesLoading,
    rateModesEvents,
    dicCurrencies,
    // periodTypes,
    priceUnits,
  } = useAppSelector((state) => state.rtkDictionaries);

  const dispatch = useAppDispatch();

  // Очистка ставки налога в стейте по выходу
  useEffect(
    () => () => {
      dispatch(taxTsActions.cleanTax());
    },
    [dispatch]
  );

  // const changeTax = () => {
  //   const taxRate = (Math.random() * 20).toFixed();
  //   dispatch(taxTsActions.setTax({ taxRate }));
  // };

  const { tax } = useAppSelector((state) => state.taxTs);
  const { tenantProperties } = useAppSelector((state) => state.tenants);

  // В России редактируется цена без налога, иначе - с. Флаг об этом
  const isEditPriceWithVat = useMemo(() => (tenantProperties as any)?.inputTax === "true", [
    tenantProperties,
  ]);

  const aggregationRuleListForSelect = useMemo<{ name?: string; id?: number }[]>(
    // В отсутствие aggName передается пробел, для корректного отображения в селекторе (иначе выведется id)
    () => aggregationRuleList?.map(({ aggName, id }) => ({ name: aggName || " ", id })) || [],
    [aggregationRuleList]
  );

  // Корректировка значений, при изменении списка групп тарификации
  useEffect(() => {
    const serviceRates = form.getFieldsValue().serviceRates || undefined;
    const aggregationRuleIds = aggregationRuleList?.map(({ id }) => id) || [];
    if (serviceRates) {
      const updatedRates = serviceRates.map((item) => {
        const aggregationRuleId =
          item.aggregationRuleId && aggregationRuleIds.includes(item.aggregationRuleId)
            ? item.aggregationRuleId
            : undefined;
        return { ...item, aggregationRuleId };
      });
      form.setFieldsValue({ serviceRates: updatedRates });
    }
  }, [aggregationRuleList, form]);

  const fieldNames = useMemo(
    (): Record<FieldName, string> => ({
      name: "Наименование",
      balanceTypeId: "Тип баланса",
      rateModeId: "Вид",
      rateTypeId: "Тип расчета",
      priceWithVat: `Цена с налогом ${round(tax?.taxRate || 0)}%`,
      taxRate: "Налог, %",
      price: `Цена без налога ${round(tax?.taxRate || 0)}%`,
      currencyId: "Валюта",
      // ratePeriodId: "Период тарифа",
      priceUnitId: "Единица тарификации",
      chargeNameId: "Номенклатура",
      rangeFrom: "От (шт)",
      rangeTo: "До (шт)",
      aggregationRuleId: "Группа трафика",
    }),
    [tax]
  );

  const normalizeInputNumber = (priceString?: string) => {
    if (!priceString) {
      return;
    }
    // Замена при вводе строки вида ".00" на "0.00"
    if (priceString.startsWith(".")) {
      return priceString.replace(".", "0.");
    }
    // Замена при вводе строки вида "01.00" на "1.00"
    if (Number(priceString) >= 1 && priceString.startsWith("0")) {
      return priceString.slice(1);
    }
    return priceString;
  };

  const onChangePrice = useCallback(() => {
    const serviceRates = form.getFieldValue("serviceRates");
    const taxRate = tax?.taxRate || "0";
    const taxMultiplier = Number(taxRate) / 100 + 1;
    if (Array.isArray(serviceRates)) {
      const updatedServiceRate = serviceRates.map((rateItem) => {
        const price = isEditPriceWithVat
          ? `${rateItem.priceWithVat / taxMultiplier}`
          : normalizeInputNumber(rateItem.price);
        const priceWithVat = isEditPriceWithVat
          ? normalizeInputNumber(rateItem.priceWithVat)
          : `${rateItem.price * taxMultiplier}`;
        return {
          ...rateItem,
          taxRate,
          price,
          priceWithVat,
        };
      });
      form.setFields([
        {
          name: "serviceRates",
          value: updatedServiceRate,
        },
      ]);
    }
  }, [form, tax, isEditPriceWithVat]);

  // Обновление цен и ставки налога, при изменении налога
  useEffect(() => {
    onChangePrice();
  }, [onChangePrice]);

  // Сброс ставки налога по умолчанию, когда создается на основании
  useEffect(() => {
    setTimeout(() => {
      onChangePrice();
    }, 100);
  }, [onChangePrice]);

  // const columnSpans = useMemo((): Record<FieldName, number> => {
  //   return {
  //     name: 2,
  //     balanceTypeId: 2,
  //     rateModeId: 2,
  //     rateTypeId: 2,
  //     priceWithVat: 2,
  //     taxRate: 1,
  //     price: 2,
  //     currencyId: 2,
  //     ratePeriodId: 4,
  //     priceUnitId: 2,
  //     chargeNameId: 2,
  //     rangeFrom: 1,
  //     rangeTo: 1,
  //   };
  // }, [selectedRateModeId]);

  const [widthRatio, setWidthRatio] = useState(window.innerWidth / windowWidth);

  useEffect(() => {
    const handleResize = () => {
      setWidthRatio(window.innerWidth / windowWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const columnFlexs: Record<FieldName | "btns", string> = {
    name: `${10.5 * widthRatio * 14.5}px`,
    balanceTypeId: `${6 * widthRatio * 14.5}px`,
    rateModeId: `${6 * widthRatio * 14.5}px`,
    aggregationRuleId: `${6 * widthRatio * 14.5}px`,
    rateTypeId: `${6 * widthRatio * 14.5}px`,
    priceWithVat: `${8.5 * widthRatio * 14.5}px`,
    taxRate: `${6 * widthRatio * 14.5}px`,
    price: `${8.5 * widthRatio * 14.5}px`,
    currencyId: `${6 * widthRatio * 14.5}px`,
    priceUnitId: `${9.5 * widthRatio * 14.5}px`,
    chargeNameId: `${6.5 * widthRatio * 14.5}px`,
    rangeFrom: `${4.5 * widthRatio * 14.5}px`,
    rangeTo: `${4.5 * widthRatio * 14.5}px`,
    btns: `${6 * widthRatio * 14.5}px`,
  };

  const getRequired = useCallback(
    (fieldName: FieldName, rateModeId?: RateModes): boolean => {
      const editedPriceFieldName = isEditPriceWithVat ? "priceWithVat" : "price";
      const allFields: FieldName[] = [
        editedPriceFieldName,
        "name",
        // "balanceTypeId",
        "rateModeId",
        "rateTypeId",
        "currencyId",
        // "ratePeriodId",
        "priceUnitId",
        "chargeNameId",
        "aggregationRuleId",
      ];
      const excludedFields: Record<RateModes, FieldName[]> = createObjectWithDefault(
        {
          [RateModes.ONE_TIME]: [
            // "ratePeriodId",
            "aggregationRuleId",
          ],
          [RateModes.PERIODIC]: [
            // "ratePeriodId",
            "aggregationRuleId",
          ],
          [RateModes.TRAFFIC]: [],
        },
        ["aggregationRuleId"]
      );
      const excludedPacketFields: Record<RateModes, FieldName[]> = createObjectWithDefault(
        {
          [RateModes.ONE_TIME]: [
            "rateTypeId",
            "priceUnitId",
            //  "ratePeriodId",
            "aggregationRuleId",
          ],
          [RateModes.PERIODIC]: [
            "rateTypeId",
            "priceUnitId",
            // "ratePeriodId",
            "aggregationRuleId",
          ],
          [RateModes.TRAFFIC]: ["rateTypeId", "priceUnitId"],
        },
        ["rateTypeId", "priceUnitId", "aggregationRuleId"]
      );
      if (!rateModeId) {
        return allFields.includes(fieldName);
      }
      if (isPacketTariff) {
        const availableFields = allFields.filter(
          (item) => !excludedPacketFields[rateModeId].includes(item)
        );
        return availableFields.includes(fieldName);
      }
      const availableFields = allFields.filter(
        (item) => !excludedFields[rateModeId].includes(item)
      );
      return availableFields.includes(fieldName);
    },
    [isPacketTariff, isEditPriceWithVat]
  );

  const getAvailable = useCallback(
    (fieldName: FieldName, rateModeId?: RateModes): boolean => {
      const editedPriceFieldName = isEditPriceWithVat ? "priceWithVat" : "price";
      const allFields: FieldName[] = [
        editedPriceFieldName,
        "name",
        "balanceTypeId",
        "rateModeId",
        "rateTypeId",
        "currencyId",
        // "ratePeriodId",
        "priceUnitId",
        "chargeNameId",
        "rangeFrom",
        "rangeTo",
        "aggregationRuleId",
      ];
      const defaultExcludeFields = ["balanceTypeId", "aggregationRuleId"];
      const excludedFields: Record<RateModes, FieldName[]> = createObjectWithDefault(
        {
          [RateModes.ONE_TIME]: [
            "balanceTypeId",
            "aggregationRuleId",
            "priceUnitId",
            "rangeFrom",
            "rangeTo",
          ],
          [RateModes.PERIODIC]: [
            "balanceTypeId",
            "aggregationRuleId",
            // "ratePeriodId",
            "rangeFrom",
            "rangeTo",
          ],
          [RateModes.TRAFFIC]: ["balanceTypeId", "priceUnitId"],
        },
        defaultExcludeFields
      );

      const excludedPacketFields: Record<RateModes, FieldName[]> = createObjectWithDefault(
        {
          [RateModes.ONE_TIME]: [
            "balanceTypeId",
            "aggregationRuleId",
            "priceUnitId",
            // "ratePeriodId",
            "rangeFrom",
            "rangeTo",
          ],
          [RateModes.PERIODIC]: [
            "balanceTypeId",
            "aggregationRuleId",
            // "ratePeriodId",
            "rangeFrom",
            "rangeTo",
          ],
          [RateModes.TRAFFIC]: ["balanceTypeId", "priceUnitId"],
        },
        defaultExcludeFields
      );
      if (isPacketTariff) {
        const availableFields = allFields.filter((item) => {
          if (rateModeId) {
            return !excludedPacketFields?.[rateModeId].includes(item);
          }
          return !defaultExcludeFields.includes(item);
        });
        return availableFields.includes(fieldName);
      }
      const availableFields = allFields.filter((item) => {
        if (rateModeId) {
          return !excludedFields[rateModeId].includes(item);
        }
        return !defaultExcludeFields.includes(item);
      });
      return availableFields.includes(fieldName);
    },
    [isPacketTariff, isEditPriceWithVat]
  );

  const getFieldLabel = (field: FieldName, idx: number): ReactNode => {
    if (idx) return null;
    return fieldNames[field];
  };

  const getFieldProps = (field: FieldName, idx: number, rateModeId?: RateModes) => ({
    className: "form-item-required__hidden-error__mb0",
    rules: [{ required: getRequired(field, rateModeId) }],
    name: [idx, field],
    label: getFieldLabel(field, idx),
  });

  const rowLayout = {
    gutter: 13 * widthRatio,
    style: {
      marginTop: "10px",
    },
  };

  const onChangeRateModeId = (rateModeId: RateModes, idx: number) => {
    // Приходится дважды заменять значения в rangeFrom/rangeTo, иначе не очищается форма (при попытке сразу установить undefined)
    form.setFields([
      {
        name: ["serviceRates", idx, "rangeFrom"],
        value: "",
        errors: [],
      },
      {
        name: ["serviceRates", idx, "rangeTo"],
        value: "",
        errors: [],
      },
      {
        name: ["serviceRates", idx, "aggregationRuleId"],
        value: undefined,
      },
      {
        name: ["serviceRates", idx, "priceUnitId"],
        value: getPriceUnitId(isPacketTariff, rateModeId),
      },
      {
        name: ["serviceRates", idx, "rateTypeId"],
        value: getRateTypeId(rateModeId),
      },
    ]);
    setTimeout(() => {
      form.setFields([
        {
          name: ["serviceRates", idx, "rangeFrom"],
          value: undefined,
        },
        {
          name: ["serviceRates", idx, "rangeTo"],
          value: undefined,
        },
      ]);
    }, 100);
  };

  return (
    <div className="serviceRate-edit">
      {/* <Button onClick={changeTax}>Change tax</Button> */}
      <Form.Item className="form-item-required__hidden-error__mb0" shouldUpdate>
        {({ setFieldsValue }) => {
          return (
            <Form.List name="serviceRates">
              {(fields, { remove }) => {
                const onRemove = (idx: number) => {
                  remove(idx);
                };
                const serviceRates = form.getFieldsValue().serviceRates || [];
                const onCopyPrice = (idx: number) => {
                  const updatedServiceRates = [
                    ...serviceRates.slice(0, idx),
                    serviceRates[idx],
                    ...serviceRates.slice(idx),
                  ];
                  setFieldsValue({ serviceRates: updatedServiceRates });
                };

                return (
                  <div>
                    {fields.map(({ key, ...field }, idx) => {
                      const rateModeId = serviceRates[idx]?.rateModeId;
                      const allowedRateTypes = getAllowedRateTypes(rateType, rateModeId);
                      const allowedPriceUnits = getAllowedPriceUnits(
                        priceUnits,
                        isPacketTariff,
                        rateModeId
                      );
                      return (
                        <Row key={key} {...rowLayout}>
                          <Col flex={columnFlexs.name}>
                            <Form.Item {...field} {...getFieldProps("name", idx, rateModeId)}>
                              <Input
                                style={{ width: columnFlexs.name }}
                                disabled={
                                  !getAvailable("name", rateModeId) ||
                                  offerListSelectMode === "select"
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col flex={columnFlexs.balanceTypeId}>
                            <Form.Item
                              {...field}
                              {...getFieldProps("balanceTypeId", idx, rateModeId)}
                            >
                              <Select
                                disabled={
                                  !getAvailable("balanceTypeId", rateModeId) ||
                                  offerListSelectMode === "select"
                                }
                                options={getSelectOptions({
                                  list: fakeBalanceType,
                                })}
                                style={{ width: columnFlexs.balanceTypeId }}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex={columnFlexs.rateModeId}>
                            <Form.Item {...field} {...getFieldProps("rateModeId", idx, rateModeId)}>
                              <Select
                                options={getSelectOptions({
                                  list: rateModesEvents,
                                })}
                                style={{ width: columnFlexs.rateModeId }}
                                disabled={
                                  !getAvailable("rateModeId", rateModeId) ||
                                  offerListSelectMode === "select"
                                }
                                onSelect={(id: RateModes) => onChangeRateModeId(id, idx)}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex={columnFlexs.aggregationRuleId}>
                            <Form.Item
                              {...field}
                              {...getFieldProps("aggregationRuleId", idx, rateModeId)}
                            >
                              <Select
                                options={getSelectOptions({
                                  list: aggregationRuleListForSelect,
                                })}
                                style={{ width: columnFlexs.aggregationRuleId }}
                                disabled={
                                  !getAvailable("aggregationRuleId", rateModeId) ||
                                  offerListSelectMode === "select"
                                }
                                // onSelect={(id: number) => onChangeRateModeId(id, idx)}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex={columnFlexs.rateTypeId}>
                            <Form.Item {...field} {...getFieldProps("rateTypeId", idx, rateModeId)}>
                              <Select
                                allowClear
                                options={getSelectOptions({
                                  list: allowedRateTypes,
                                })}
                                loading={checkIsLoading(rateTypeLoading)}
                                disabled={
                                  !getAvailable("rateTypeId", rateModeId) ||
                                  offerListSelectMode === "select" ||
                                  allowedRateTypes.length === 1
                                }
                                style={{ width: columnFlexs.rateTypeId }}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex={columnFlexs.priceWithVat}>
                            <Form.Item
                              {...field}
                              {...getFieldProps("priceWithVat", idx, rateModeId)}
                              normalize={(value) => value.replace(/ /g, "")}
                              className="form-item-required__hidden-error__mb0"
                            >
                              <NumberFormat
                                thousandSeparator={" "}
                                className="ant-input-affix-wrapper inputNumber__hideArrows"
                                decimalScale={2}
                                fixedDecimalScale
                                allowNegative={hasAccess(OFFERING_PRICE_NEGATIVEAMOUNT)}
                                disabled={
                                  !getAvailable("priceWithVat", rateModeId) ||
                                  offerListSelectMode === "select"
                                }
                                onChange={onChangePrice}
                                onValueChange={onChangePrice}
                                // onInput={normalizeInputNumber}
                                // onBlur={normalizeInputNumber}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex={columnFlexs.taxRate}>
                            <Tooltip
                              title={
                                tax ? "" : "Для расчета налога задайте дату создания и тип продукта"
                              }
                            >
                              <Form.Item {...field} {...getFieldProps("taxRate", idx, rateModeId)}>
                                <NumberFormat
                                  thousandSeparator={" "}
                                  className="ant-input-affix-wrapper inputNumber__hideArrows"
                                  decimalScale={2}
                                  fixedDecimalScale
                                  disabled
                                />
                              </Form.Item>
                            </Tooltip>
                          </Col>
                          <Col flex={columnFlexs.price}>
                            <Form.Item
                              {...field}
                              normalize={(value) => value.replace(/ /g, "")}
                              {...getFieldProps("price", idx, rateModeId)}
                            >
                              <NumberFormat
                                thousandSeparator={" "}
                                className="ant-input-affix-wrapper inputNumber__hideArrows"
                                decimalScale={2}
                                fixedDecimalScale
                                allowNegative={hasAccess(OFFERING_PRICE_NEGATIVEAMOUNT)}
                                onChange={onChangePrice}
                                onValueChange={onChangePrice}
                                disabled={
                                  !getAvailable("price", rateModeId) ||
                                  offerListSelectMode === "select"
                                }
                                // onInput={normalizeInputNumber}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex={columnFlexs.currencyId}>
                            <Form.Item {...field} {...getFieldProps("currencyId", idx, rateModeId)}>
                              <Select
                                options={getSelectOptions({
                                  list: dicCurrencies,
                                })}
                                style={{ width: columnFlexs.currencyId }}
                                disabled={
                                  !getAvailable("currencyId", rateModeId) ||
                                  offerListSelectMode === "select"
                                }
                              />
                            </Form.Item>
                          </Col>
                          {/* <Col flex={columnFlexs.ratePeriodId}>
                            <Form.Item
                              {...field}
                              {...getFieldProps("ratePeriodId", idx, rateModeId)}
                            >
                              <Select
                                options={getSelectOptions({
                                  list: periodTypes,
                                })}
                                style={{ width: columnFlexs.ratePeriodId }}
                                disabled={
                                  !getAvailable("ratePeriodId", rateModeId) ||
                                  offerListSelectMode === "select"
                                }
                              />
                            </Form.Item>
                          </Col> */}
                          <Col flex={columnFlexs.priceUnitId}>
                            <Form.Item
                              {...field}
                              {...getFieldProps("priceUnitId", idx, rateModeId)}
                            >
                              <Select
                                options={getSelectOptions({
                                  list: allowedPriceUnits,
                                })}
                                style={{ width: columnFlexs.priceUnitId }}
                                disabled={
                                  !getAvailable("priceUnitId", rateModeId) ||
                                  offerListSelectMode === "select" ||
                                  allowedPriceUnits.length === 1
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col flex={columnFlexs.chargeNameId}>
                            <Form.Item
                              {...field}
                              {...getFieldProps("chargeNameId", idx, rateModeId)}
                              style={{ width: columnFlexs.chargeNameId }}
                            >
                              <Select
                                allowClear
                                options={getSelectOptions({
                                  list: chargeNames,
                                })}
                                loading={checkIsLoading(chargeNamesLoading)}
                                disabled={
                                  !getAvailable("chargeNameId", rateModeId) ||
                                  offerListSelectMode === "select"
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col flex={columnFlexs.rangeFrom}>
                            <Form.Item
                              {...field}
                              {...getFieldProps("rangeFrom", idx, rateModeId)}
                              dependencies={[["serviceRates", idx, "rangeTo"]]}
                              normalize={(value) => value.replace(/ /g, "")}
                              rules={[
                                {
                                  validator: (_, value) => {
                                    if (
                                      rangeFromLimitValidator({
                                        rangeFrom: value,
                                        limitTo: serviceRates[idx].rangeTo,
                                      })
                                    ) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject();
                                  },
                                },
                              ]}
                              className="form-item-required__hidden-error__mb0"
                            >
                              <NumberFormat
                                thousandSeparator={" "}
                                className="ant-input-affix-wrapper inputNumber__hideArrows"
                                decimalScale={0}
                                fixedDecimalScale
                                onBlur={(event) => {
                                  const rangeFrom = event.target.value.replace(/ /g, "");
                                  const isValueValid = rangeFromLimitValidator({
                                    rangeFrom,
                                    limitTo: serviceRates[idx].rangeTo,
                                  });
                                  if (!isValueValid)
                                    message.warning(
                                      'Значение "От" не должно быть больше значения "До"'
                                    );
                                }}
                                disabled={
                                  !getAvailable("rangeFrom", rateModeId) ||
                                  offerListSelectMode === "select"
                                }
                                allowNegative={false}
                              />
                            </Form.Item>
                          </Col>
                          <Col flex={columnFlexs.rangeTo}>
                            <Form.Item
                              {...field}
                              {...getFieldProps("rangeTo", idx, rateModeId)}
                              dependencies={[["serviceRates", idx, "rangeFrom"]]}
                              normalize={(value) => value.replace(/ /g, "")}
                              rules={[
                                {
                                  validator: (_, rangeTo) => {
                                    if (
                                      rangeToLimitValidator({
                                        rangeTo,
                                        limitFrom: serviceRates[idx].rangeFrom,
                                      })
                                    ) {
                                      return Promise.resolve();
                                    }
                                    return Promise.reject();
                                  },
                                },
                              ]}
                              className="form-item-required__hidden-error__mb0"
                            >
                              <NumberFormat
                                thousandSeparator={" "}
                                className="ant-input-affix-wrapper inputNumber__hideArrows"
                                decimalScale={0}
                                fixedDecimalScale
                                onBlur={(event) => {
                                  const rangeTo = event.target.value.replace(/ /g, "");
                                  const isValueValid = rangeToLimitValidator({
                                    limitFrom: serviceRates[idx].rangeFrom,
                                    rangeTo,
                                  });
                                  if (!isValueValid)
                                    message.warning(
                                      'Значение "До" не должно быть меньше значения "От"'
                                    );
                                }}
                                disabled={
                                  !getAvailable("rangeTo", rateModeId) ||
                                  offerListSelectMode === "select"
                                }
                                allowNegative={false}
                              />
                            </Form.Item>
                          </Col>
                          {offerListSelectMode !== "select" && (
                            <Col flex={columnFlexs.btns}>
                              <div style={{ paddingTop: idx ? "0px" : "50px" }}>
                                <CopyIcon
                                  className="serviceRate-edit__copyButton"
                                  onClick={() => onCopyPrice(idx)}
                                />
                                <DeleteIcon
                                  className="serviceRate-edit__deleteButton"
                                  onClick={() => {
                                    if (fields.length === 1 && onRemoveLastRate) {
                                      onRemoveLastRate();
                                    }
                                    onRemove(idx);
                                  }}
                                />
                              </div>
                            </Col>
                          )}
                        </Row>
                      );
                    })}
                  </div>
                );
              }}
            </Form.List>
          );
        }}
      </Form.Item>
    </div>
  );
};
